import { ReactNode, useContext } from 'react';
import { IListExpansion } from 'src/hooks/useListExpansion';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  ActionOutboxEntityType,
  CatalogResults,
  EventWithData,
  Feature,
  Listing,
  PurchaseOrderTicketGroup,
  Sale,
} from 'src/WebApiController';
import { useContextSelector } from 'use-context-selector';

import {
  CatalogDataContextV2,
  CatalogDataContextWithSelector,
  ICatalogDataContext,
} from './CatalogDataContext.types';

const useContextValueSelector = <T>(
  selector: (context: ICatalogDataContext) => T
): T => {
  const hasCatalogDataContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const contextValue = useContextSelector(
    CatalogDataContextWithSelector,
    selector
  );
  const context = useContext(CatalogDataContextV2);

  return hasCatalogDataContextWithSelectorFeature
    ? contextValue
    : selector(context);
};

export const useCatalogDataContextIsLoadingSelector = (): boolean => {
  return useContextValueSelector((context) => context.isLoading);
};

export const useCatalogDataContextIsItemsLoadingSelector = (): boolean => {
  return useContextValueSelector((context) => context.isItemsLoading);
};

export const useCatalogDataContextEventsSelector = ():
  | EventWithData[]
  | undefined => {
  return useContextValueSelector((context) => context.eventsTransformed);
};

export const useCatalogDataContextDataSelector = ():
  | CatalogResults
  | undefined => {
  return useContextValueSelector((context) => context.data);
};

export const useCatalogDataContextTotalPurchaseCountSelector = ():
  | number
  | undefined => {
  return useContextValueSelector((context) => context.totalPurchaseCount);
};

export const useCatalogDataContextLogKeySelector = (): string => {
  return useContextValueSelector((context) => context.logKey);
};

export const useCatalogDataContextUpdateItemInEventSelector = (): ((
  item: Listing | Sale | PurchaseOrderTicketGroup,
  entityType: ActionOutboxEntityType,
  sectionalUpdate?: boolean
) => void) => {
  return useContextValueSelector((context) => context.updateItemInEvent);
};

export const useCatalogDataContextErrorInfoSelector = ():
  | { errorHeader: ReactNode; errorMessage: ReactNode }
  | undefined => {
  return useContextValueSelector((context) => context.errorInfo);
};

export const useCatalogDataContextEventsExpansionSelector =
  (): IListExpansion<string> => {
    return useContextValueSelector((context) => context.eventsExpansion);
  };

export const useCatalogDataContextallEventIdsSelector = ():
  | {
      viagVirtId: string;
      performerId: number | null;
      venueId: number;
    }[]
  | undefined => {
  return useContextValueSelector((context) => context.allEventIds);
};
